import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, X, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * FavoritesPanel - Shows user's favorite reports/pages in a slide-out panel
 */
export default function FavoritesPanel() {
  const [open, setOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = async () => {
    const token = localStorage.getItem('qapi-token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/quick-wins/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFavorites();
    }
  }, [open]);

  const removeFavorite = async (reportId) => {
    const token = localStorage.getItem('qapi-token');
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/quick-wins/favorites/${reportId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setFavorites(favorites.filter(f => f.report_id !== reportId));
      }
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-1.5 relative"
          data-testid="favorites-panel-btn"
        >
          <Star className="h-4 w-4" />
          <span className="hidden sm:inline">Favorites</span>
          {favorites.length > 0 && (
            <Badge 
              variant="secondary" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]"
            >
              {favorites.length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            Your Favorites
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading...
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8">
              <Star className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No favorites yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Click the star icon on any page to add it here
              </p>
            </div>
          ) : (
            favorites.map((fav) => (
              <Card key={fav.report_id} className="group hover:border-primary transition-colors">
                <CardContent className="p-3 flex items-center justify-between">
                  <Link 
                    to={fav.report_path}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 flex-1 hover:text-primary"
                    data-testid={`favorite-link-${fav.report_id}`}
                  >
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 shrink-0" />
                    <span className="text-sm font-medium truncate">{fav.report_name}</span>
                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-50" />
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100"
                    onClick={() => removeFavorite(fav.report_id)}
                    data-testid={`remove-favorite-${fav.report_id}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
